import React from 'react'
import { Link } from 'react-router-dom'
import { Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { DEFAULT_SUPPLIERS_UK_TIMEZONE } from 'src/constants/defaultSuppliers'
import { formatInviteDate } from 'src/utils/supplierInvitesStorage'

dayjs.extend(utc)
dayjs.extend(timezone)

export const DEFAULT_SUPPLIERS_EMPTY_STATE_COPY =
  'All caught up — no venues are still on default supplier pricing.'

export const DEFAULT_SUPPLIERS_CONNECTED_COLUMN_TITLE = 'Date/Time Connected'

export const HAS_REAL_SUPPLIER_ACTIVE_WARNING =
  'A real supplier with the same name is also active — a price file may already exist.'

export const DEFAULT_SUPPLIERS_TABLE_DEFAULT_SORT = {
  columnKey: 'daysOnDefault',
  order: 'descend',
}

export const DEFAULT_SUPPLIERS_TONE_TAG_COLORS = {
  green: 'green',
  amber: 'orange',
  red: 'red',
  muted: 'default',
}

/**
 * Whole UK calendar days since connection (mirrors express defaultSuppliers helper).
 */
export function getDaysOnDefault(
  connectedAt,
  now = new Date(),
  ukTimezone = DEFAULT_SUPPLIERS_UK_TIMEZONE,
) {
  if (!connectedAt) {
    return null
  }

  const connected = dayjs(connectedAt).tz(ukTimezone).startOf('day')
  const reference = dayjs(now).tz(ukTimezone).startOf('day')

  if (!connected.isValid() || !reference.isValid()) {
    return null
  }

  return reference.diff(connected, 'day')
}

export function getHoursOnDefault(connectedAt, now = new Date()) {
  if (!connectedAt) {
    return null
  }

  const connected = dayjs(connectedAt)
  const reference = dayjs(now)

  if (!connected.isValid() || !reference.isValid()) {
    return null
  }

  return reference.diff(connected, 'hour')
}

/**
 * SLA tone from hours on default pricing: green < 24h, amber 24–48h, red > 48h.
 */
export function getDaysOnDefaultTone(hoursOnDefault) {
  if (hoursOnDefault == null || Number.isNaN(hoursOnDefault)) {
    return 'muted'
  }

  if (hoursOnDefault < 24) {
    return 'green'
  }

  if (hoursOnDefault <= 48) {
    return 'amber'
  }

  return 'red'
}

export function formatConnectedDate(isoString) {
  if (!isoString) {
    return '—'
  }

  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }

  const formatted = formatInviteDate(isoString)
  return formatted === 'Invalid date' ? '—' : formatted
}

export function getDefaultSupplierVenueHref(venueId) {
  return venueId ? `/v/${venueId}/suppliers` : null
}

export function formatDaysOnDefaultLabel(days) {
  if (days == null || Number.isNaN(days)) {
    return '—'
  }

  return `${days} day${days === 1 ? '' : 's'}`
}

export function mapDefaultSupplierTableRows(rows = []) {
  return rows.map(row => ({
    ...row,
    key: row.venueSupplierId,
  }))
}

export function sortDefaultSuppliersOldestFirst(rows = []) {
  return [...rows].sort((left, right) => {
    const leftDays = left.daysOnDefault ?? -1
    const rightDays = right.daysOnDefault ?? -1

    if (rightDays !== leftDays) {
      return rightDays - leftDays
    }

    return String(left.connectedAt || '').localeCompare(
      String(right.connectedAt || ''),
    )
  })
}

export function filterDefaultSuppliersBySupplierName(
  rows = [],
  searchTerm = '',
) {
  const query = String(searchTerm || '')
    .trim()
    .toLowerCase()

  if (!query) {
    return rows
  }

  return rows.filter(row =>
    String(row.supplierName || '')
      .toLowerCase()
      .includes(query),
  )
}

export function prepareDefaultSuppliersTableRows(rows = [], searchTerm = '') {
  return mapDefaultSupplierTableRows(
    filterDefaultSuppliersBySupplierName(
      sortDefaultSuppliersOldestFirst(rows),
      searchTerm,
    ),
  )
}

export function renderDefaultSupplierVenueCell(venueName, record) {
  const href = getDefaultSupplierVenueHref(record?.venueId)

  if (!href) {
    return venueName || 'Unknown Venue'
  }

  return <Link to={href}>{venueName || 'Unknown Venue'}</Link>
}

export function renderDefaultSupplierDaysBadge(
  days,
  connectedAt,
  now = new Date(),
) {
  const hoursOnDefault = getHoursOnDefault(connectedAt, now)
  const tone = getDaysOnDefaultTone(hoursOnDefault)

  return (
    <Tag color={DEFAULT_SUPPLIERS_TONE_TAG_COLORS[tone]}>
      {formatDaysOnDefaultLabel(days)}
    </Tag>
  )
}

export function renderDefaultSupplierWarning(record) {
  if (!record?.hasRealSupplierActive) {
    return null
  }

  return (
    <Tooltip title={HAS_REAL_SUPPLIER_ACTIVE_WARNING}>
      <Tag color='gold'>Real supplier active</Tag>
    </Tooltip>
  )
}

export function buildDefaultSuppliersTableColumns({ now = new Date() } = {}) {
  return [
    {
      title: 'Venue',
      dataIndex: 'venueName',
      key: 'venueName',
      className: 'restrictShrink',
      render: (venueName, record) =>
        renderDefaultSupplierVenueCell(venueName, record),
      sorter: (left, right) =>
        String(left.venueName || '')
          .trim()
          .localeCompare(String(right.venueName || '').trim()),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName',
      className: 'restrictShrink',
      render: (supplierName, record) => (
        <>
          {supplierName || '—'}
          {renderDefaultSupplierWarning(record)}
        </>
      ),
      sorter: (left, right) =>
        String(left.supplierName || '')
          .trim()
          .localeCompare(String(right.supplierName || '').trim()),
    },
    {
      title: 'Email',
      dataIndex: 'supplierEmail',
      key: 'supplierEmail',
      className: 'restrictShrink',
      render: value => value || '—',
    },
    {
      title: 'Account Number',
      dataIndex: 'accountNumber',
      key: 'accountNumber',
      className: 'restrictShrink',
      render: value => value || '—',
    },
    {
      title: DEFAULT_SUPPLIERS_CONNECTED_COLUMN_TITLE,
      dataIndex: 'connectedAt',
      key: 'connectedAt',
      className: 'restrictShrink',
      render: value => formatConnectedDate(value),
      sorter: (left, right) =>
        new Date(left.connectedAt || 0) - new Date(right.connectedAt || 0),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Days on default',
      dataIndex: 'daysOnDefault',
      key: 'daysOnDefault',
      className: 'restrictShrink',
      render: (days, record) =>
        renderDefaultSupplierDaysBadge(days, record.connectedAt, now),
      sorter: (left, right) =>
        (left.daysOnDefault ?? -1) - (right.daysOnDefault ?? -1),
      defaultSortOrder: DEFAULT_SUPPLIERS_TABLE_DEFAULT_SORT.order,
    },
  ]
}
